import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ErrorBoundary from '../components/ErrorBoundary.jsx';
import { Shell } from '../components/Shell';
import { Sidebar } from '../components/Sidebar';
import Navbar from '../components/Navbar';
import { fetchCoverage } from '../components/EntryFlowModal';
import { useT } from '../i18n';

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const shiftMonth = (period, delta) => {
  if (!/^\d{4}-\d{2}$/.test(period || '')) return period; // cleared month input → no-op
  const [y, m] = period.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// Non-React module scope: month names resolve from the stored language directly
// (same pattern as utils/auth.js).
const MONTHS_SQ = localStorage.getItem('k360-lang') === 'en'
  ? ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
  : ['Janar', 'Shkurt', 'Mars', 'Prill', 'Maj', 'Qershor', 'Korrik', 'Gusht', 'Shtator', 'Tetor', 'Nëntor', 'Dhjetor'];
const monthLabel = (p) => `${MONTHS_SQ[Number(p.slice(5, 7)) - 1]} ${p.slice(0, 4)}`;

/**
 * Regjistrimi mujor — the guided flow the data scientist asked for:
 * Scope 1 (stacionare → mobile → proceset) → Scope 2 → Scope 3, each step
 * showing its status and total for the month; once every scope has data,
 * the month summary appears. Reports/Detailed/Invoices remain the full views —
 * this screen only drives the current registration.
 */
export const MonthlyFlowPage = () => {
  const navigate = useNavigate();
  const { t } = useT();
  const [period, setPeriod] = useState(getCurrentMonth());
  const [cov, setCov] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchCoverage(period)
      .then(d => { if (alive) { setCov(d); setLoading(false); } })
      .catch(() => { if (alive) { setCov(null); setLoading(false); } });
    return () => { alive = false; };
  }, [period]);

  const totals = cov?.totals || { byCategory: {} };
  const steps = cov ? [
    { n: 1, scope: 'Scope 1', label: t('mflow.stepStationary'), done: cov.scope1.stationary > 0, count: cov.scope1.stationary, total: totals.byCategory.stationary || 0, to: '/scope-1/djegja-stacionare' },
    { n: 2, scope: 'Scope 1', label: t('mflow.stepMobile'), done: cov.scope1.mobile > 0, count: cov.scope1.mobile, total: totals.byCategory.mobile || 0, to: '/scope-1/djegja-mobile' },
    { n: 3, scope: 'Scope 1', label: t('mflow.stepProcess'), done: cov.scope1.process > 0, count: cov.scope1.process, total: totals.byCategory.process || 0, to: '/scope-1/emetimet-nga-proceset', optional: true },
    { n: 4, scope: 'Scope 2', label: t('mflow.stepElectricity'), done: (cov.scope2.electricity + cov.scope2.heating + cov.scope2.ev) > 0, count: cov.scope2.electricity + cov.scope2.heating + cov.scope2.ev, total: totals.scope2 || 0, to: '/scope-2/electricity' },
    { n: 5, scope: 'Scope 3', label: t('mflow.stepTravel'), done: (cov.scope3.business_travel + cov.scope3.employee_commuting + cov.scope3.other) > 0, count: cov.scope3.business_travel + cov.scope3.employee_commuting + cov.scope3.other, total: totals.scope3 || 0, to: '/scope-3' }
  ] : [];

  const doneCount = steps.filter(s => s.done).length;
  const firstMissing = steps.find(s => !s.done && !s.optional) || steps.find(s => !s.done);
  const s1Any = cov ? (cov.scope1.stationary + cov.scope1.mobile + cov.scope1.process) > 0 : false;
  const s2Any = cov ? (cov.scope2.electricity + cov.scope2.heating + cov.scope2.ev) > 0 : false;
  const s3Any = cov ? (cov.scope3.business_travel + cov.scope3.employee_commuting + cov.scope3.other) > 0 : false;
  const monthComplete = s1Any && s2Any && s3Any;

  const card = {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 16,
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  };

  return (
    <ErrorBoundary>
      <Shell sidebar={<Sidebar />}>
        <Navbar pageTitle={t('mflow.title')} />
        <div style={{ padding: 32, maxWidth: 860, margin: '0 auto', background: '#f8f9fa', minHeight: '100vh' }}>

          {/* Month picker + progress */}
          <div style={{ ...card, padding: '18px 22px', marginBottom: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{t('mflow.heading')}</div>
                <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2 }}>
                  {t('mflow.headingSub')}
                </div>
              </div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '6px 8px', fontWeight: 700, fontSize: 13.5 }}>
                <button aria-label={t('mflow.prevMonth')} onClick={() => setPeriod(p => shiftMonth(p, -1))} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 15, color: '#6b7280', padding: '2px 8px', borderRadius: 6 }}>‹</button>
                <span style={{ minWidth: 110, textAlign: 'center' }}>{monthLabel(period)}</span>
                <button aria-label={t('mflow.nextMonth')} onClick={() => setPeriod(p => shiftMonth(p, 1))} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 15, color: '#6b7280', padding: '2px 8px', borderRadius: 6 }}>›</button>
              </div>
            </div>
            <div style={{ marginTop: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
                <span>{t('mflow.stepsDone', { done: doneCount, total: steps.length || 5 })}</span>
                {monthComplete && <span style={{ color: '#059669', fontWeight: 700 }}>{t('mflow.monthComplete')}</span>}
              </div>
              <div style={{ height: 8, borderRadius: 99, background: '#eef1f5', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${steps.length ? (doneCount / steps.length) * 100 : 0}%`, background: 'linear-gradient(90deg, #12c7a6, #3fffd8)', borderRadius: 99, transition: 'width 0.35s ease' }} />
              </div>
            </div>
          </div>

          {/* Month summary — appears once every scope has data */}
          {monthComplete && cov && (
            <div style={{ ...card, padding: '20px 22px', marginBottom: 18, borderTop: '4px solid #12c7a6' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <span style={{ width: 34, height: 34, borderRadius: 99, background: '#ecfdf5', color: '#059669', display: 'grid', placeItems: 'center', fontWeight: 900, fontSize: 16 }}>✓</span>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{t('mflow.summaryTitle', { month: monthLabel(period) })}</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>{t('mflow.summarySub')}</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
                {[
                  { l: 'Scope 1', v: totals.scope1, c: '#6366f1' },
                  { l: 'Scope 2', v: totals.scope2, c: '#0ea5e9' },
                  { l: 'Scope 3', v: totals.scope3, c: '#f59e0b' },
                  { l: t('mflow.total'), v: totals.grand, c: '#111827', big: true }
                ].map(x => (
                  <div key={x.l} style={{ background: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: 12, padding: '12px 14px' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: x.c, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{x.l}</div>
                    <div style={{ fontSize: x.big ? 24 : 20, fontWeight: 800, color: '#111827', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>
                      {(x.v || 0).toFixed(3)} <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>t CO₂e</span>
                    </div>
                  </div>
                ))}
              </div>
              {(totals.biogenicMemo || 0) > 0 && (
                <div style={{ marginTop: 10, fontSize: 12, color: '#166534', background: '#ecfdf5', border: '1px dashed #a7f3d0', borderRadius: 8, padding: '8px 11px' }}>
                  {t('mflow.biogenicMemo', { value: totals.biogenicMemo.toFixed(3) })}
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
                <button onClick={() => navigate('/emissions')} style={{ background: '#3fffd8', color: '#111827', border: 'none', borderRadius: 9, fontWeight: 700, fontSize: 13.5, padding: '11px 20px', cursor: 'pointer', minHeight: 42 }}>
                  {t('mflow.viewReports')}
                </button>
                <button onClick={() => navigate('/invoices-new')} style={{ background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 9, fontWeight: 700, fontSize: 13.5, padding: '11px 20px', cursor: 'pointer', minHeight: 42 }}>
                  {t('mflow.viewAllInvoices')}
                </button>
              </div>
            </div>
          )}

          {/* Steps */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {loading && !cov ? (
              <div style={{ ...card, padding: 22, color: '#9ca3af', fontSize: 13.5, textAlign: 'center' }}>{t('mflow.loading')}</div>
            ) : steps.map(s => {
              const isNext = firstMissing && firstMissing.n === s.n && !monthComplete;
              return (
                <div key={s.n} style={{
                  ...card,
                  padding: '16px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  flexWrap: 'wrap',
                  border: isNext ? '1.5px solid #12c7a6' : card.border
                }}>
                  <span style={{
                    width: 30, height: 30, borderRadius: 99, flexShrink: 0,
                    display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 13,
                    background: s.done ? '#ecfdf5' : '#f3f4f6',
                    color: s.done ? '#059669' : '#9ca3af'
                  }}>
                    {s.done ? '✓' : s.n}
                  </span>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#0f766e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {s.scope}{s.optional ? ` · ${t('mflow.ifApplicable')}` : ''}
                    </div>
                    <div style={{ fontSize: 14.5, fontWeight: 700, color: '#111827' }}>{s.label}</div>
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>
                      {s.done
                        ? t(s.count === 1 ? 'mflow.recordsOne' : 'mflow.recordsMany', { count: s.count, total: s.total.toFixed(3) })
                        : t('mflow.noData')}
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(s.to)}
                    style={{
                      background: isNext ? '#3fffd8' : (s.done ? '#fff' : '#f9fafb'),
                      color: isNext ? '#111827' : (s.done ? '#374151' : '#0f766e'),
                      border: isNext ? 'none' : '1px solid #e5e7eb',
                      borderRadius: 9, fontWeight: 700, fontSize: 12.5,
                      padding: '9px 16px', cursor: 'pointer', minHeight: 40, whiteSpace: 'nowrap'
                    }}
                  >
                    {isNext ? t('mflow.continueHere') : s.done ? t('mflow.addReview') : t('mflow.addData')}
                  </button>
                </div>
              );
            })}
          </div>

          <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 18, lineHeight: 1.6 }}>
            {t('mflow.footerLead')}
            {' '}<span style={{ color: '#0f766e', fontWeight: 600, cursor: 'pointer' }} onClick={() => navigate('/emissions')}>Reports</span>,
            {' '}<span style={{ color: '#0f766e', fontWeight: 600, cursor: 'pointer' }} onClick={() => navigate('/emissions/detailed')}>{t('mflow.detailedReport')}</span>{' '}{t('mflow.and')}
            {' '}<span style={{ color: '#0f766e', fontWeight: 600, cursor: 'pointer' }} onClick={() => navigate('/invoices-new')}>Invoices</span>.
          </p>
        </div>
      </Shell>
    </ErrorBoundary>
  );
};

export default MonthlyFlowPage;
